/**
 * The trading day the shop has open.
 *
 * Separate from a cashier's shift: a day can hold a morning and an evening
 * shift, and it stays open — past midnight and on — until someone closes it.
 * Closing rolls the date to whatever the shop says the next day is, so
 * late-night trade stays on the night it belongs to rather than the calendar
 * flipping it at midnight.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { today } from '@foodrun/shared';

import { api } from '../../lib/api.js';

export function useBusinessDay() {
  const client = useQueryClient();

  const current = useQuery({
    queryKey: ['business-day', 'current'],
    queryFn: () => api.get('/pos/business-day/current'),
    // The takings in it move with every sale, so it is not held long.
    staleTime: 15_000,
  });

  const close = useMutation({
    mutationFn: (body) => api.post('/pos/business-day/close', body),
    onSuccess: () => {
      // The day changed, and so did what "today" means for every report.
      client.invalidateQueries({ queryKey: ['business-day'] });
      client.invalidateQueries({ queryKey: ['reports'] });
    },
  });

  return { current, close };
}

/**
 * The trading day the shop has open — what "today" means on every sales
 * screen. The shop trades past midnight, so the calendar is the wrong answer
 * after 12. Until the till has heard from the server it is null, and screens
 * keep whatever day they had; once it arrives, a screen that has not been
 * touched moves to it.
 */
export function useTradingDay() {
  const { current } = useBusinessDay();
  return current.data?.open?.businessDate ?? null;
}

/** A date state that starts on the trading day once it is known, unless the person has picked one. */
export function useTradingDayState() {
  const tradingDay = useTradingDay();
  const [picked, setPicked] = useState(null);
  const day = picked ?? tradingDay ?? today();
  return [day, setPicked, tradingDay];
}
